/**
 * Owner proofs (docs/architecture/sync.md, "Vault repair"): the server's list
 * of an account's maps is the reference, and a map missing from the vault is
 * put back from it, but only with a proof that only the owner's devices can
 * make. The server could otherwise add a map of its own (a random key locked
 * with the account's public key, which it has).
 *
 * proof = HMAC-SHA256(K, "orbital:map-owner:v1|<docId>")
 * K = HKDF-SHA256(master key, info "orbital:map-owner-proof:v1"), used for
 * nothing else. The master key never leaves the account's devices in clear.
 * The proof is sent with the map's registration (POST /api/docs). It does
 * not cover the map's key, so key rotations never affect it.
 */

const INFO = "orbital:map-owner-proof:v1";
const LABEL = "orbital:map-owner:v1";
const encoder = new TextEncoder();

const bytesOf = (base64) => Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
const base64Of = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)));

/**
 * The proof key, derived from the master key (raw bytes).
 * @param {Uint8Array} masterKey
 * @returns {Promise<CryptoKey>}
 */
export async function ownerProofKey(masterKey) {
  const base = await crypto.subtle.importKey("raw", masterKey, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: encoder.encode(INFO) },
    base,
    { name: "HMAC", hash: "SHA-256", length: 256 },
    false,
    ["sign", "verify"],
  );
}

const message = (docId) => encoder.encode(`${LABEL}|${docId}`);

/**
 * @param {CryptoKey} key - ownerProofKey
 * @param {string} docId
 * @returns {Promise<string>} base64 proof
 */
export async function ownerProof(key, docId) {
  return base64Of(await crypto.subtle.sign("HMAC", key, message(docId)));
}

/** Whether `proof` is this account's proof for this map. */
export async function isOwnerProofValid(key, docId, proof) {
  if (typeof proof !== "string" || !proof) return false;
  try {
    return await crypto.subtle.verify("HMAC", key, bytesOf(proof), message(docId));
  } catch {
    return false;
  }
}
